/**
 * Item and skill art: which `icon_id` has a picture, and where that picture is.
 *
 * Distinct from `ui/icons.ts`, which is the closed set of *interface* icons — a portal
 * marker, a stash, a skull — drawn as FontAwesome paths so the same shape works in the
 * DOM and on a canvas. These are the opposite kind of thing: hundreds of painted 128px
 * squares, one per catalogue entry, and the client never draws them anywhere but an
 * `<img>`.
 *
 * ── Why the art lives under `src/` when every other image lives in `public/` ──
 *
 * Because the client has to answer *do I have this one?* — and it has to answer it
 * before rendering, not after a request fails.
 *
 * Art arrives one generated sheet at a time. Every catalogue entry names an `icon_id`
 * already (234 of them), and only some have been drawn, so the common case is an id with
 * no file. `public/` is copied to the output verbatim and is invisible from inside the
 * bundle, which leaves two bad options: a hand-kept list of filenames, which is the
 * directory written twice and drifts the first time somebody adds a sheet, or an `<img>`
 * onError handler, which means every undrawn item flashes a broken image and then
 * replaces it.
 *
 * `import.meta.glob` under `src/` is the third option and it cannot drift: the set of
 * drawn icons **is** the set of files on disk, resolved when the bundle is built. Adding
 * a sheet is `slice-icons.py` and nothing else. Vite hashes and emits each file the
 * ordinary way, and `eager` here costs nothing at runtime — the values are URL strings,
 * and the browser fetches an icon only when one is actually rendered.
 */

const FILES = import.meta.glob<string>('../assets/icons/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

/**
 * `icon_id` → URL, keyed by the filename with the extension removed.
 *
 * **The filename is the id.** `slice-icons.py` writes `<icon_id>.webp` straight from the
 * manifest and there is no lookup table on either side, which is the property that makes
 * a wrong name impossible rather than merely unlikely: there is nowhere for a second
 * spelling to live.
 */
const BY_ID = new Map<string, string>(
  Object.entries(FILES).map(([path, url]) => [
    path.slice(path.lastIndexOf('/') + 1).replace(/\.webp$/, ''),
    url,
  ]),
);

/**
 * The URL for an icon, or `undefined` when that id has no art yet.
 *
 * `undefined` is a normal, expected answer and every caller has to handle it by drawing
 * the glyph — a `†` for a sword is legible, and a missing image is not. It is also the
 * answer for an id the server sent that this build does not know, which is what a client
 * running against a newer server sees.
 */
export function iconArtUrl(iconId: string | undefined): string | undefined {
  return iconId ? BY_ID.get(iconId) : undefined;
}

/** Every id with art, for tests and for the icon-coverage report. */
export function drawnIconIds(): string[] {
  return [...BY_ID.keys()].sort();
}
